/*
================================================================================
                    WI PDF MAIN - BUSINESS LOGIC
================================================================================

PURPOSE: Welding Instruction slip PDF generation with dual-page format

KEY FEATURES:
• Dual-page WI slip: Page 1 (Supervisor copy), Page 2 (Stores copy)
• Dynamic content rendering based on welding process type
• Reprint tracking with date stamping and counter display
• Process-specific parameter display (GTAW, SMAW, FCAW, SAW)
• Filler metal specifications with brand and batch tracking

DATA STRUCTURE:
• Header data: Welder info, project details, slip metadata
• Detail data: WPS parameters, process specifications, gas settings
• Multi-layer welding parameters with array-based storage
• Notes formatting with line break handling (max 6 lines)

BUSINESS RULES:
• Groove welds display thickness limits and layer parameters
• Tubesheet welds show specialized OTV parameters
• Overlay welds include base metal thickness specifications
• Flux details conditionally displayed based on process requirements
• Reprint watermark appears on subsequent prints with date

PDF STRUCTURE:
• Page 1: Complete welding parameters for supervisor reference
• Page 2: Consumable requisition for stores management
• Consistent formatting across all welding process types
• Company branding and regulatory compliance information

================================================================================
*/

import { Col, Row } from "react-bootstrap";
import "../Css/groove_pdf.css";

/*
 * WiPdfMain Component - WI slip PDF content generation
 * 
 * COMPONENT WORKFLOW:
 * 1. Parse header and detail data from WI slip record
 * 2. Format dates and handle reprint logic
 * 3. Render process-specific parameters and specifications
 * 4. Generate dual-page layout for supervisor and stores
 * 5. Apply conditional formatting based on welding type
 */
const WiPdfMain = ({ rowdata }) => {
  let header = rowdata[0];
  let detail = rowdata[1];
  let slipDate = header?.WI_SlipDate;
  let date = new Date(slipDate);
  let formattedDate =
    date.getDate() + "/" + (date.getMonth() + 1) + "/" + date.getFullYear();
  const tradeName = detail?.WPSD_FM_ChemicalAnalysis_TradeName || '';
  const firstPart = tradeName?.slice(0, 18);
  const secondPart = tradeName?.slice(17);
  const AllNotes = header?.WI_NOTES.split('\n');
  const Notes = AllNotes?.slice(0, 6);
  const WeldDepThick = header?.WI_WeldDepThick.split("|")

  let WI_WeldDepThick = "NA";
  let wi_Weld_Max = "NA";

  if (detail.wld_WPSType === "Groove") {
    wi_Weld_Max = header?.WI_Weld_Max + " mm";

    if (WeldDepThick[0] === "9999.00") {
      WI_WeldDepThick = "NO Limit";
    }
    else {
      WI_WeldDepThick = WeldDepThick[0];
    }
  }

  return (
    <div className="MainPDF">
      <header>
        <table className="tablenoborder">
          <div style={{ display: "flex" }}>
            <tr>
              <td className="tablenoborder godrejTitle alignTextLeft" valign="top" style={{ fontWeight: "200" }}>
                Godrej & Boyce Mfg. Co. Ltd
              </td>
            </tr>
            <tr>
              <td className="tablenoborder godrejAddress alignTextRight"
                valign="top"
                style={{ position: "relative", left: "390px" }}
              >
                {header?.WI_print >= 1
                  ? `Reprinted On: ${new Date().toLocaleDateString("en-GB")}`
                  : ""}{" "}
              </td>
            </tr>
          </div>
          <tr>
            <td className="tablenoborder godrejAddress alignTextRight"
              style={{ position: "relative", right: "50px" }}
            >
              Creation Date {formattedDate}
            </td>
          </tr>
          <tr>
            <td
              className="tablenoborder pageTitle"
              align="center"
              valign="top"
              style={{ verticalAlign: "top", padding: 0, height: ".1%" }}
            >
              Welder Consumable Requisition /Instruction Slip
            </td>
          </tr>
          <div className="borderline"></div>
        </table>
        <div style={{ display: "flex" }}>
          <div style={{ display: "flex" }}>
            <div className="ReportHeadersleft alignTextLeft">
              Welder:
            </div>
            <div className="Reportdataleft alignTextLeft"
              style={{ width: "250px" }}
            >
              {header?.WI_Welder_Id}/{header?.WI_Welder_Name}
            </div>
          </div>
          <div style={{ display: "flex" }}>
            <div className="Reportslipheadright alignTextRight">
              Slip No:
            </div>
            <div className="Reportslipnodata alignTextRight">
              PED/{header?.WI_plant}/{header?.SK}
            </div>
          </div>
        </div>
        <div style={{ display: "flex" }}>
          <div style={{ display: "flex" }}>
            <div className="ReportHeadersleft alignTextLeft">
              Project:
            </div>
            <div className="Reportdataleft alignTextLeft"
              style={{ width: "120px" }}
            >
              {header?.WI_Project_No}
            </div>
          </div>
          <div style={{ display: "flex" }}>
            <div className="ReportHeadersright alignTextRight">
              Seam No:
            </div>
            <div className="Reportdataright alignTextRight">
              {header?.WI_Joint_No}
            </div>
          </div>
        </div>
        <div style={{ display: "flex" }}>
          <div style={{ display: "flex" }}>
            <div className="ReportHeadersleft alignTextLeft">
              WPS No:
            </div>
            <div className="Reportdataleft alignTextLeft"
              style={{ width: "120px" }}
            >
              {detail?.WPSH_WPS_No}
            </div>
          </div>
          <div style={{ display: "flex" }}>
            <div className="ReportHeadersmiddle alignTextLeft">
              {" "}
              Rev No:
            </div>
            <div className="Reportdatamiddle alignTextLeft"
              style={{ width: "65px" }}
            >
              {" "}
              {detail?.WPSH_WPS_Revi_No}{" "}
            </div>
          </div>
          <div style={{ display: "flex" }}>
            <div className="ReportHeadersright1 alignTextRight">
              Welding Position:
            </div>
            <div className="Reportdataright1 alignTextRight">
              {header?.WI_Welding_Position}
            </div>
          </div>
        </div>
        <div style={{ display: "flex" }}>
          <div style={{ display: "flex" }}>
            <div className="ReportHeadersleft alignTextLeft">
              Max Groove Weld metal thk.(WPS):{" "}
            </div>
            <div className="Reportdataleft alignTextLeft"
              style={{ width: "90px" }}>
              &nbsp;{wi_Weld_Max}
            </div>
          </div>
          <div style={{ display: "flex" }}>
            <div className="ReportHeadersrightProcess alignTextRight">
              Process:
            </div>
            <div className="ReportdatarightProcess alignTextRight">
              {detail?.WPSDProcessCat} / {detail?.WPSDType}
            </div>
          </div>
        </div>
        <div style={{ display: "flex" }}>
          <div style={{ display: "flex" }}>
            <div className="ReportHeadersleft alignTextLeft">
              Qualified max Groove Weld metal thk.(WPQ):{" "}
            </div>
            <div className="Reportdataleft alignTextLeft"
              style={{ width: "60px" }}
            >
              {WI_WeldDepThick}
            </div>
          </div>
          <div style={{ display: "flex" }}>
            <div className="ReportHeadersright2 alignTextRight">
              Joint Design:
            </div>
            <div className="Reportdataright2 alignTextRight">
              As per drawing
            </div>
          </div>
        </div>
        <div>
          <br></br>
          <div>
            <div className="ReportHeadersleft alignTextLeft">
              Instructions:
            </div>
          </div>
        </div>
        <Row style={{ display: "flex" }}>
          <Col md={4} style={{ display: "flex", width: "390px" }}>
            (1) Min. Preheat Temp.: {detail.WPSD_WP_MinimumPreheatTemp} °C
          </Col>
          <Col md={4} style={{ display: "flex" }}>
            (4) Wrap in Insulation Material : {header?.WI_Wrap_Insulation}
          </Col>
        </Row>
        <Row style={{ display: "flex" }}>
          <Col md={4} style={{ display: "flex", width: "390px" }}>
            (2) Max. Interpass Temp.: {detail.WPSD_WP_MaximumInterpassTemp} °C
          </Col>
          <Col md={4} style={{ display: "flex" }}>
            (5) Electrodes drawn must be kept in portable ovens
          </Col>
        </Row>
        <Row style={{ display: "flex" }}>
          <Col md={6} style={{ display: "flex" }}>
            (3) Post Heat Temp.: {header.WI_Pre_Heat_Temp}&nbsp;<b> °C for</b>&nbsp;{header.WI_Pre_Heat_Time}&nbsp;<b>min</b>
          </Col>
        </Row>
        <br></br>
        <table className="Table50">
          <tbody>
            <tr>
              <th
                className="wider-cell1"
                style={{
                  border: "none",
                  borderTop: "1px solid black",
                  borderLeft: "1px solid black",
                  width: "330px",
                }}
              >
                <span className="tabledata">Filler Metal: AWS:</span>
                <td className="tableheadingd" style={{ borderLeft: "0px", fontWeight: "100" }}>{header?.WI_FM_AWS}</td>
              </th>
              <th style={{ textAlign: "center", borderRight: "0" }}>
                <span className="tableheading">
                  Diameter <br></br>(mm){" "}
                </span>
              </th>
              <th style={{ textAlign: "center", width: "190px", borderRight: "0" }}>
                <span className="tableheading" style={{ paddingBottom: "10px" }}>
                  Batch No
                </span>
              </th>
              <th style={{ textAlign: "center", borderRight: "0" }}>
                <span className="tableheading">Layer</span>
              </th>
              <th style={{ textAlign: "center", borderRight: "0" }}>
                <span className="tableheading">Current Polarity</span>
              </th>
              <th style={{ textAlign: "center", borderRight: "0" }}>
                <span className="tableheading">Current (Ampere)</span>
              </th>
              <th style={{ textAlign: "center", borderRight: "0" }}>
                <span className="tableheading">Voltage (Volts)</span>
              </th>
              <th style={{ textAlign: "center", borderRight: "0" }}>
                <span className="tableheading">
                  Speed <br></br>(mm/min)
                </span>
              </th>
              <th style={{ textAlign: "center", width: "30px" }}>
                <span className="tableheading">Qty Iss.</span>
              </th>
            </tr>
            <tr>
              <td className="tabledata" style={{ borderLeft: "1px solid black" }}>
                Brand:
              </td>
              <td className="tableheadingd" style={{ borderLeft: "1px solid black" }}>
                {header?.WI_FillerMetalSize[0]}
              </td>
              <td className="tableheadingd">{header?.WI_WP_BatchNumber[0]} </td>
              <td className="tableheadingd">
                {detail?.WPSD_WP_LayerNumber[0] ? detail?.WPSD_WP_LayerNumber[0] : detail?.WPSH_WeldLayer}
              </td>
              <td className="tableheadingd">
                {detail?.WPSD_WP_CurrentPolarity[0]
                  ? detail?.WPSD_WP_CurrentPolarity[0]
                  : ""}
              </td>
              <td className="tableheadingd">
                {detail?.WPSD_WP_Amperes[0] ? detail?.WPSD_WP_Amperes[0] : ""}
              </td>
              <td className="tableheadingd">
                {detail?.WPSD_WP_Volts[0] ? detail?.WPSD_WP_Volts[0] : ""}
              </td>
              <td className="tableheadingd">
                {detail?.WPSD_WP_TravelSpeed[0]
                  ? detail?.WPSD_WP_TravelSpeed[0]
                  : ""}
              </td>
              <td className="tableheadingd"></td>
            </tr>
            <tr>
              <td className="tableheadingd"> {firstPart}</td>
              <td
                className="tableheadingd"
                style={{ borderLeft: "1px solid black" }}
              >
                {header.WI_FillerMetalSize[1]}
              </td>
              <td className="tableheadingd">{header?.WI_WP_BatchNumber[1]}</td>
              <td className="tableheadingd">
                {detail?.wld_WPSType === "Groove"
                  ? detail?.WPSD_WP_LayerNumber[1]
                  : ""}
              </td>
              <td className="tableheadingd">
                {detail?.WPSD_WP_CurrentPolarity[1]
                  ? detail?.WPSD_WP_CurrentPolarity[1]
                  : ""}
              </td>
              <td className="tableheadingd">
                {detail?.WPSD_WP_Amperes[1] ? detail?.WPSD_WP_Amperes[1] : ""}
              </td>
              <td className="tableheadingd">
                {detail?.WPSD_WP_Volts[1] ? detail?.WPSD_WP_Volts[1] : ""}
              </td>
              <td className="tableheadingd">
                {detail?.WPSD_WP_TravelSpeed[1]
                  ? detail?.WPSD_WP_TravelSpeed[1]
                  : ""}
              </td>
              <td className="tableheadingd"></td>
            </tr>
            <tr>
              <td
                className="tableheadingd">
                {secondPart || ""}
              </td>
              <td
                className="tableheadingd"
                style={{ borderLeft: "1px solid black" }}
              >
                {header.WI_FillerMetalSize[2]}
              </td>
              <td className="tableheadingd">{header?.WI_WP_BatchNumber[2]}</td>
              <td className="tableheadingd">
                {detail?.wld_WPSType === "Groove"
                  ? detail?.WPSD_WP_LayerNumber[2]
                  : ""}
              </td>
              <td className="tableheadingd">
                {detail?.WPSD_WP_CurrentPolarity[2]
                  ? detail?.WPSD_WP_CurrentPolarity[2]
                  : ""}
              </td>
              <td className="tableheadingd">
                {detail?.WPSD_WP_Amperes[2] ? detail?.WPSD_WP_Amperes[2] : ""}
              </td>
              <td className="tableheadingd">
                {detail?.WPSD_WP_Volts[2] ? detail?.WPSD_WP_Volts[2] : ""}
              </td>
              <td className="tableheadingd">
                {detail?.WPSD_WP_TravelSpeed[2]
                  ? detail?.WPSD_WP_TravelSpeed[2]
                  : ""}
              </td>
              <td className="tableheadingd"></td>
            </tr>
          </tbody>
        </table>
        <table className="Table50">
          <tbody>
            <tr>
              <th className="wider-cell" style={{ borderTop: "0px" }}>
                <span
                  className="tabledata"
                  style={{
                    borderRight: "0px",
                    borderLeft: "0px",
                    borderTop: "0px",
                  }}
                >
                  Gas
                </span>
              </th>
              <th
                className="wider-cel"
                style={{
                  width: "90px",
                  borderTop: "0px",
                  borderLeft: "0px",
                  borderRight: "1px solid black",
                }}
              >
                <span className="tabledata">Type / Composition</span>
              </th>
              <th
                className="wider-ce"
                style={{
                  width: "90px", borderTop: "0px", borderLeft: "0px",
                }}
              >
                <span className="tabledata">
                  Flow Rate <br></br>(Litres / min.)
                </span>
              </th>
              <th
                className="wider-ce"
                style={{
                  border: "none",
                  borderRight: "1px solid black",
                  borderTop: "0px",
                  width: "160px",
                }}
              >
                <span className="tableheading2">Flux : {header?.WI_Flux}</span>
              </th>
            </tr>

            <tr>
              <td
                className="tableheadingL"
                style={{ borderRight: "1px solid black" }}
              >
                Shielding
              </td>
              <td
                className="tableheadingL"
                style={{ borderRight: "1px solid black" }}
              >
                {detail?.WPSD_WP_ShieldingGasType}
              </td>
              <td
                className="tableheadingL"
                style={{ borderRight: "1px solid black" }}
              >
                {detail?.WPSD_WP_ShieldingFlowRate}
              </td>
              <td
                className="tableheadingL"
                style={{ borderRight: "1px solid black" }}
              >
                Brand: {header?.WI_Flux_Brand}
              </td>
            </tr>
            <tr>
              <td
                className="tableheadingL"
                style={{ borderRight: "1px solid black" }}
              >
                Backing
              </td>
              <td
                className="tableheadingL"
                style={{ borderRight: "1px solid black" }}
              >
                {detail?.WPSD_WP_BackingGasType}
              </td>
              <td
                className="tableheadingL"
                style={{ borderRight: "1px solid black" }}
              >
                {detail?.WPSD_WP_BackingFlowRate}
              </td>
              <td
                className="tableheadingL"
                style={{ borderRight: "1px solid black" }}
              >
                Flux Item: {header?.WI_Flux_ItemCode}
              </td>
            </tr>
            <tr>
              <td
                className="tableheadingL"
                style={{ borderRight: "1px solid black" }}
              >
                Trailing
              </td>
              <td
                className="tableheadingL"
                style={{ borderRight: "1px solid black" }}
              >
                {detail?.WPSD_WP_TrailingGasType}
              </td>
              <td
                className="tableheadingL"
                style={{ borderRight: "1px solid black" }}
              >
                {detail?.WPSD_WP_TrailingFlowRate}
              </td>
              <td
                className="tableheadingL"
                style={{ borderRight: "1px solid black" }}
              >
                Flux Batch: {header?.WI_Flux_Batch}
              </td>
            </tr>
          </tbody>
        </table>
        <br></br>
        <div >
          <div className="ReportHeadersleft alignTextLeft">
            Notes:
          </div>
          <div className="Reportdataleftnotes alignTextLeft"
            style={{ width: "800px" }}>
            {Notes.map((point, index) => (
              <>{point}<br></br></>
            ))}
          </div>
        </div>
        <br></br>
        <div style={{ display: "flex", position: "relative", top: "20px" }}>
          <div style={{ display: "flex" }}>
            <div className="ReportHeadersleft alignTextLeft">
              (Shopfloor Supervisor )
            </div>
          </div>
        </div>
        <table className="tablenoborder" style={{ position: "relative", top: "50px" }}>
          <tr>
            <td
              className="tablenoborder pageTitle"
              align="center"
              valign="top"
              style={{ verticalAlign: "top", padding: 0, height: ".1%", borderBottom: "1px solid black", borderTop: "1px solid black" }}
            >
              Welder Consumable Requisition /Instruction Slip
            </td>
          </tr>
        </table>
        <div style={{ position: "relative", top: "55px" }}>
          <div style={{ display: "flex" }}>
            <div className="Reportslipheadrightdate alignTextRight">
              Creation Date {formattedDate}
            </div>
          </div>
          <div style={{ display: "flex" }}>
            <div style={{ display: "flex" }}>
              <div className="ReportHeadersleft alignTextLeft">
                Welder:
              </div>
              <div className="Reportdataleft alignTextLeft" style={{ width: "250px" }}>
                {header?.WI_Welder_Id}/{header?.WI_Welder_Name}
              </div>
            </div>
            <div style={{ display: "flex" }}>
              <div className="Reportslipheadright alignTextRight">
                Slip No:
              </div>
              <div className="Reportslipnodata alignTextRight">
                {header?.WI_Slip}
              </div>
            </div>
          </div>
          <div style={{ display: "flex" }}>
            <div style={{ display: "flex" }}>
              <div className="ReportHeadersleft alignTextLeft">
                Project:
              </div>
              <div className="Reportdataleft alignTextLeft" style={{ width: "120px" }}>
                {header?.WI_Project_No}
              </div>
            </div>
            <div style={{ display: "flex" }}>
              <div className="ReportHeadersright alignTextRight">
                Seam No:
              </div>
              <div className="Reportdataright alignTextRight">
                {header?.WI_Joint_No}
              </div>
            </div>
          </div>
          <div style={{ display: "flex" }}>
            <div style={{ display: "flex" }}>
              <div className="ReportHeadersleft alignTextLeft">
                WPS No:
              </div>
              <div className="Reportdataleft alignTextLeft" style={{ width: "120px" }}>
                {detail?.WPSH_WPS_No}
              </div>
            </div>
            <div style={{ display: "flex" }}>
              <div className="ReportHeadersright1pg2 alignTextRight">
                Rev No:
              </div>
              <div className="Reportdataright1pg2 alignTextRight">
                {detail?.WPSH_WPS_Revi_No}
              </div>
            </div>
            <div style={{ display: "flex" }}>
              <div className="ReportHeadersright2pg2 alignTextRight">
                Process:
              </div>
              <div className="Reportdataright2pg2 alignTextRight">
                {detail?.WPSDProcessCat} / {detail?.WPSDType}
              </div>
            </div>
          </div>
          <br></br>
          <table className="Table50">
            <tbody>
              <tr>
                <th className="wider-cell3" style={{ width: "200px" }}>
                  <span className="tabledata"></span>
                </th>
                <th className="wider-cell" style={{ borderRight: "1px solid black", borderLeft: "0" }} >
                  <span className="tableheading " style={{ borderRight: "0" }}>Diameter (mm)</span>
                </th>
                <th className="wider-cell" style={{ borderLeft: "0" }}>
                  <span className="tableheading" style={{ borderLeft: "0" }}>Batch No</span>
                </th>
                <th className="wider-cell2" style={{ borderLeft: "0" }}>
                  <span className="tableheading">Item Code</span>
                </th>
                <th className="wider-cell" style={{ borderLeft: "0" }}>
                  <span className="tableheading">Quantity Issued</span>
                </th>
              </tr>
              <tr>
                <td
                  className="tabledatad"
                  style={{ borderRight: "1px solid black" }}
                >
                  Filler Metal (AWS): {header?.WI_FM_AWS}
                </td>
                <td
                  className="tabledatad"
                  style={{ borderRight: "1px solid black" }}
                >
                  {header?.WI_FillerMetalSize[0]}
                </td>
                <td
                  className="tabledatad"
                  style={{ borderRight: "1px solid black" }}
                >
                  {header?.WI_WP_BatchNumber[0]}                </td>
                <td
                  className="tabledatad"
                  style={{ borderRight: "1px solid black" }}
                >
                  {header?.WI_WeldConsumable[0] ? header?.WI_WeldConsumable[0] : ""}
                </td>
                <td
                  className="tabledatad"
                  style={{ borderRight: "1px solid black" }}
                >
                </td>
              </tr>
              <tr>
                <td
                  className="tabledatad"
                  style={{ borderRight: "1px solid black" }}
                >
                  Brand: {header?.WI_FM_Brand}
                </td>
                <td
                  className="tabledatad"
                  style={{ borderRight: "1px solid black" }}
                >
                  {header?.WI_FillerMetalSize[1]}
                </td>
                <td
                  className="tabledatad"
                  style={{ borderRight: "1px solid black" }}
                >
                  {header?.WI_WP_BatchNumber[1]}
                </td>
                <td
                  className="tabledatad"
                  style={{ borderRight: "1px solid black" }}
                >
                  {header?.WI_WeldConsumable[1] ? header?.WI_WeldConsumable[1] : ""}
                </td>
                <td
                  className="tabledatad"
                  style={{ borderRight: "1px solid black" }}
                >
                </td>
              </tr>
              <tr>
                <td
                  className="tabledatad"
                  style={{
                    borderRight: "1px solid black",
                    borderBottom: "1px solid black",
                  }}
                ></td>
                <td
                  className="tabledatad"
                  style={{
                    borderRight: "1px solid black",
                    borderBottom: "1px solid black",
                  }}
                >
                  {header?.WI_FillerMetalSize[2]}
                </td>
                <td
                  className="tabledatad"
                  style={{
                    borderRight: "1px solid black", borderBottom: "1px solid black",
                  }}
                >
                  {header?.WI_WP_BatchNumber[2]}
                </td>
                <td
                  className="tabledatad"
                  style={{
                    borderRight: "1px solid black",
                    borderBottom: "1px solid black",
                  }}
                >
                  {header?.WI_WeldConsumable[2] ? header?.WI_WeldConsumable[2] : ""}
                </td>
                <td
                  className="tabledatad"
                  style={{
                    borderRight: "1px solid black",
                    borderBottom: "1px solid black",
                  }}
                >
                </td>
              </tr>
              <tr>
                <td
                  className="tabledatad"
                  style={{ borderRight: "1px solid black" }}
                >
                  Flux: {header?.WI_Flux}{" "}
                </td>
                <td
                  className="tabledatad"
                  colSpan={2}
                  style={{
                    borderRight: "none",
                  }}
                >
                  Item Code:
                </td>
                <td
                  className="tabledatad"
                  style={{
                    position: "relative", right: "50px",
                    borderRight: "none",
                  }}
                >
                  {header?.WI_Flux_ItemCode}
                </td>
                <td
                  className="tabledatad"
                  style={{
                    borderRight: "none",
                  }}
                ></td>
              </tr>
              <tr>
                <td
                  className="tabledatad"
                  style={{ borderRight: "1px solid black" }}
                >
                  Brand: {header?.WI_Flux_Brand}
                </td>
                <td
                  className="tabledatad"
                  colSpan={2}
                >
                  Quantity Issued:
                </td>
                <td
                  className="tabledatad"
                ></td>
                <td className="tabledatad" style={{ borderRight: "1px solid black" }}
                ></td>
              </tr>
            </tbody>
          </table>

          <div className="ReportHeadersleft alignTextLeft" style={{ position: "relative", top: "30px" }} >
            (Stores Incharge)
          </div>
        </div>
      </header >
    </div >
  );
};

export default WiPdfMain;